// Realtime cache invalidation. Single WebSocket connection from the React app
// to the ws-server, mediated by `useRealtime()` mounted at App root.
//
// Flow per message:
//   server → { type: "entity_changed", entity, op, id }
//   client → invalidate every queryKey listed under ENTITY_DEPS[entity]
//   React Query → refetch in the background → UI updates
//
// The dependency schema (ENTITY_DEPS) lives in `entity-deps.ts` so it can be
// unit-tested independently of this WebSocket machinery.

import { useEffect, useRef, useState } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api } from "./api.js";
import { depsFor } from "./entity-deps.js";

const WS_URL = import.meta.env.VITE_WS_URL ?? "wss://finance-ws.kostanos.com";

export type RealtimeStatus = "idle" | "connecting" | "live" | "reconnecting" | "offline";

interface EntityChangedMessage {
  type: "entity_changed";
  entity: string;
  op: "insert" | "update" | "delete";
  id: string;
  at: string;
}

interface HelloMessage { type: "hello"; uid: string }

type IncomingMessage = HelloMessage | EntityChangedMessage;

function applyInvalidation(qc: QueryClient, msg: EntityChangedMessage): void {
  for (const key of depsFor(msg.entity)) {
    void qc.invalidateQueries({ queryKey: key });
  }
}

/**
 * Mount once at the App root. Returns the current connection status so the
 * sidebar can render an indicator dot.
 */
export function useRealtime(): RealtimeStatus {
  const qc = useQueryClient();
  const [status, setStatus] = useState<RealtimeStatus>("idle");
  // Refs survive React StrictMode double-effects without spawning two sockets.
  const socketRef = useRef<WebSocket | null>(null);
  const backoffRef = useRef(1000);
  const tokenRefreshRef = useRef<number | null>(null);
  const reconnectRef = useRef<number | null>(null);
  const stoppedRef = useRef(false);

  useEffect(() => {
    stoppedRef.current = false;

    async function connect(): Promise<void> {
      if (stoppedRef.current) return;
      if (document.hidden) {
        // Don't waste a slot while user is on another tab. Resume on visibilitychange.
        return;
      }
      setStatus("connecting");
      let token: string;
      let exp: number;
      try {
        const t = await api.wsToken();
        token = t.token;
        exp = t.exp;
      } catch {
        // Auth expired or api offline — back off and try again later.
        scheduleReconnect();
        return;
      }

      const ws = new WebSocket(`${WS_URL}/?t=${encodeURIComponent(token)}`);
      socketRef.current = ws;

      ws.addEventListener("open", () => {
        setStatus("live");
        backoffRef.current = 1000;
        // Refresh token 30s before exp so the next reconnect (e.g. after laptop
        // wake) doesn't fail.
        const msUntilRefresh = Math.max(10_000, exp * 1000 - Date.now() - 30_000);
        if (tokenRefreshRef.current) clearTimeout(tokenRefreshRef.current);
        tokenRefreshRef.current = window.setTimeout(() => { ws.close(4000, "token-refresh"); }, msUntilRefresh);
      });
      ws.addEventListener("message", (ev) => {
        let msg: IncomingMessage | null = null;
        try {
          msg = JSON.parse(ev.data as string) as IncomingMessage;
        } catch {
          return;
        }
        if (msg.type === "entity_changed") applyInvalidation(qc, msg);
      });
      const handleClose = () => {
        socketRef.current = null;
        if (tokenRefreshRef.current) {
          clearTimeout(tokenRefreshRef.current);
          tokenRefreshRef.current = null;
        }
        scheduleReconnect();
      };
      ws.addEventListener("close", handleClose);
      ws.addEventListener("error", handleClose);
    }

    function scheduleReconnect(): void {
      if (stoppedRef.current) return;
      setStatus(socketRef.current ? "reconnecting" : "offline");
      const delay = Math.min(backoffRef.current, 30_000);
      backoffRef.current = Math.min(backoffRef.current * 2, 30_000);
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      reconnectRef.current = window.setTimeout(() => { void connect(); }, delay);
    }

    function onVisibilityChange(): void {
      if (document.hidden) return;
      // Tab visible again: if not live, retry now (don't wait full backoff).
      if (socketRef.current?.readyState !== WebSocket.OPEN) {
        if (reconnectRef.current) clearTimeout(reconnectRef.current);
        backoffRef.current = 1000;
        void connect();
      }
    }

    document.addEventListener("visibilitychange", onVisibilityChange);
    void connect();

    return () => {
      stoppedRef.current = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (tokenRefreshRef.current) clearTimeout(tokenRefreshRef.current);
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      socketRef.current?.close(1000, "unmount");
      socketRef.current = null;
    };
  }, [qc]);

  return status;
}
